import { useConfigStore } from '@/stores';
import styles from './styles.module.scss';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { useOrientation, useTickSoundInteraction } from '@/hooks';
import type { PoiContentSlide } from '@/types/shop';
import type { LocalizedString } from '@/services/dto/kiosk-config';
import PlaceHolder from '@/components/icons/shop/PlaceHolder';
import { memo, useCallback, useMemo } from 'react';
import { AppUtils } from '@/utils/app';
import poiUtils from '@/utils/poi';

interface ShopCarouselItemProps {
    content: PoiContentSlide;
    tabIndex: number;
    id?: string;
    onClick?: (data: PoiContentSlide) => void;
}

const ShopCarouselItem = memo(({ content, tabIndex, id, onClick }: ShopCarouselItemProps) => {
    const contrast = useConfigStore((state) => state.config.ui.contrast);
    const screenMode = useConfigStore((state) => state.config.ui.screenMode);
    const language = useConfigStore((state) => state.config.ui.language);
    const mainLanguage = useConfigStore((state) => state.config.ui.mainLanguage);
    const buildings = useConfigStore((state) => state.config.server?.store?.buildings);
    const isExhibitionCompany = useConfigStore((state) => state.config.server?.company?.companyType === '3');
    const searchMenu = useConfigStore((state) => {
        const config = state.config;
        return (
            config.server?.buildingMenus?.[config.ui.language]?.find((menu) => menu.menuType === 'SEARCH') ??
            config.server?.buildingMenus?.[config.ui.mainLanguage]?.find((menu) => menu.menuType === 'SEARCH')
        );
    });
    const { t } = useTranslation();
    const { isHorizontal } = useOrientation();
    const { handleInteraction, handleKeyboardInteraction } = useTickSoundInteraction();

    const categoryNames = useMemo(() => {
        const building = buildings?.find((b) => b.studioBuildingId === content.data.studioBuildingId);

        return poiUtils.getPoiCategoryCodes(content.data).flatMap((code) => {
            // API trả về name dạng LocalizedString dù DTO khai báo là string
            const name = building?.buildingCategories.find((category) => category.categoryCode === code)?.name as LocalizedString | string | undefined;
            const localizedName = typeof name === 'string' ? name : (name?.[language] ?? name?.[mainLanguage]);
            return localizedName ? [localizedName] : [];
        });
    }, [buildings, content.data, language, mainLanguage]);

    const ariaLabel = useMemo(() => {
        const parts: string[] = [content.name];

        // Tạm ẩn hiển thị keyword khi search text có match
        // if (content.keyword) {
        //     parts.push(t('shop.slide.hashtag', { hashtag: content.keyword.replaceAll('#', '') }));
        // }

        if (searchMenu?.showBuildingName !== false && buildings?.length && buildings.length > 1 && content.buildingName) {
            parts.push(content.buildingName);
        }
        parts.push(t('shop.search.tts.content.floor', { floor: AppUtils.getFloorTTSText(content.floor) }));

        if (isExhibitionCompany && searchMenu?.showBoothNumber !== false && content.boothNumber) {
            parts.push(t('shop.search.tts.content.booth', { booth: content.boothNumber }));
        }
        return parts.join(', ');
    }, [
        buildings?.length,
        content.boothNumber,
        content.buildingName,
        content.floor,
        // content.keyword,
        content.name,
        isExhibitionCompany,
        searchMenu?.showBoothNumber,
        searchMenu?.showBuildingName,
        t,
    ]);

    const handleClick = useCallback(
        (e: React.MouseEvent<HTMLDivElement>) => {
            const parent = e.currentTarget;
            parent.classList.add('poi-active');
            handleInteraction(e, () => onClick?.(content));
        },
        [handleInteraction, onClick, content]
    );

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => handleKeyboardInteraction(e, () => onClick?.(content)),
        [handleKeyboardInteraction, onClick, content]
    );

    return (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <div
            key={content.id}
            id={id}
            tabIndex={tabIndex}
            data-interactive
            data-playsound="ting"
            data-role={t('common.button.tts.popup_button')}
            className={clsx(styles.wrapper, {
                [styles['wrapper__contrast']]: contrast,
                [styles['wrapper__vertical--halfmode']]: !isHorizontal && screenMode === 'split',
                [styles['wrapper__vertical']]: !isHorizontal,
            })}
            aria-label={ariaLabel}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
        >
            {searchMenu?.showThumbnail !== false && (
                <div
                    className={clsx(styles['wrapper__poi--image'], {
                        [styles['wrapper__poi--image__vertical--halfmode']]: !isHorizontal && screenMode === 'split',
                    })}
                >
                    {content.url ? (
                        <img src={content.url} alt={`${content.name} logo`} loading="lazy" data-role={t('common.data_role.image')} />
                    ) : (
                        <PlaceHolder />
                    )}
                </div>
            )}

            <div
                className={clsx(styles['wrapper__poi--info'], {
                    [styles['wrapper__poi--info__contrast']]: contrast,
                    [styles['wrapper__poi--info__vertical--halfmode']]: !isHorizontal && screenMode === 'split',
                })}
            >
                <div className={clsx(styles['wrapper__poi--info--meta'])}>
                    <span className={styles['wrapper__poi--info--meta--floor']}>{content.floor}</span>
                    {searchMenu?.showBuildingName !== false && buildings?.length && buildings.length > 1 && content.buildingName ? (
                        <span className={styles['wrapper__poi--info--meta--building']}>· {content.buildingName}</span>
                    ) : null}
                    {isExhibitionCompany && searchMenu?.showBoothNumber !== false && content.boothNumber ? (
                        <span className={styles['wrapper__poi--info--meta--booth']}>{content.boothNumber}</span>
                    ) : null}
                </div>
                <h3
                    className={clsx(styles['wrapper__poi--info--title'], {
                        [styles['wrapper__poi--info--title--clamp-1']]: !isHorizontal && screenMode === 'split',
                    })}
                >
                    {content.name}
                </h3>
                {searchMenu?.showCategory !== false && categoryNames.length > 0 && (
                    <div className={styles['wrapper__poi--info--category']}>
                        {categoryNames.map((name) => (
                            <span key={name} className={styles['wrapper__poi--info--category--item']}>
                                {name}
                            </span>
                        ))}
                    </div>
                )}
                {/* Tạm ẩn hiển thị keyword khi search text có match
                {content.keyword && (
                    <div className={styles['wrapper__poi--info--keyword']}>
                        <p>{content.keyword}</p>
                    </div>
                )}
                */}
            </div>
        </div>
    );
});

ShopCarouselItem.displayName = 'ShopCarouselItem';

export default ShopCarouselItem;
